import React, { useRef, useState } from "react";

import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { GridColumnMenu } from "@mui/x-data-grid";
import AttachmentIcon from "@mui/icons-material/Attachment";
import AddIcon from "@mui/icons-material/Add";

import { useFormik } from "formik";
import * as Yup from "yup";

import PropTypes from "prop-types";
import { uniqueId } from "lodash";

import Modal from "Components/Common/Modal";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import InputFieldText from "Components/Common/InputFieldText";
import Backdrop from "Components/Common/Backdrop";

import removeExtension from "utils/removeExtension";
import getFileExtension from "utils/getFileExtension";
import { generateFormData } from "utils";

import {
  useFireSafetybuildingInfoQuery,
  usePostBuildingSendMailMutation,
} from "apis/FireSafety/FireSafetySendMail/index";

import pdfImg from "assets/images/pdf.png";

import { DocumentName, StyledGrid } from "pages/UtilityConsumers/style";
import styled from "@emotion/styled";

import { MuiChipsInput } from "mui-chips-input";

const validFileFormats = ["jpg", "png", "jpeg", "pdf"];

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const CustomColumnMenu = (props) => (
  <GridColumnMenu
    {...props}
    slots={{
      columnMenuColumnsItem: null,
      columnMenuSortItem: null,
    }}
  />
);

const convertTextToHtmlParagraphs = (text = "") =>
  text
    .split(/\r?\n/)
    .map((line) => `<p>${line || ""}</p>`)
    .join("");

const columns = [
  {
    headerName: "Building Name",
    valueGetter: (params) => params.row?.fireSafetyBuilding?.name || "-",
    width: 350,
  },
  {
    headerName: "User Name",
    field: "userName",
    width: 350,
  },
  {
    headerName: "Email",
    field: "email",
    width: 350,
  },
];

const initialValues = {
  subject: "",
  body: "",
  attachments: [],
  recipientEmails: [],
  ccEmails: [],
};

const StyledTextField = styled(MuiChipsInput)`
  .MuiInputBase-root {
    padding: 5px 10px 0 10px;
  }
`;

const SendBuildingMailModal = ({ handleClose, t }) => {
  const ccRef = useRef();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [openMailForm, setOpenMailForm] = useState(false);
  const [documentToPreview, setDocumentToPreview] = useState(null);

  const {
    data: { data: buildingInfo = [] } = {},
    isLoading: isBuildingInfoLoading,
  } = useFireSafetybuildingInfoQuery();

  const [sendMail, { isLoading: isMailSending }] =
    usePostBuildingSendMailMutation();

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      subject: Yup.string().required("Subject is required"),
      body: Yup.string().required("Description is required"),
      ccEmails: Yup.array().test("isValidCCEmails", "", function () {
        const [{ value: current = {} } = {}] = this.from || [];
        const { ccEmails = [], recipientEmails = [] } = current;

        if (
          ccEmails.length &&
          !ccEmails.every((email) => emailRegex.test(email))
        ) {
          return this.createError({ message: "Enter valid CC email." });
        }

        if (new Set(ccEmails).size !== ccEmails.length) {
          return this.createError({
            message: "Duplicate CC emails are not allowed",
          });
        }

        return true;
      }),
    }),
    onSubmit: async ({
      subject,
      body,
      attachments,
      recipientEmails,
      ccEmails,
    }) => {
      try {
        const formattedBody = convertTextToHtmlParagraphs(body);

        const formData = new FormData();

        generateFormData(formData, {
          subject,
          body: formattedBody,
          recipientEmails,
          ccEmails,
          IsForFireSafety: true,
        });

        if (attachments.length) {
          attachments.forEach(({ document }) => {
            formData.append("attachments", document);
          });
        }

        await sendMail(formData);
        handleClose();
      } catch (error) {}
    },
  });

  const {
    values: { attachments, recipientEmails, ccEmails },
    handleSubmit,
    setFieldValue,
  } = formik;

  const fileChangeHandler = (event) => {
    const files = Array.from(event.target.files);

    const isValid = files.every(({ name }) =>
      validFileFormats.includes(getFileExtension(name.toLowerCase()))
    );

    if (!isValid) {
      toast.error("Invalid file format, Please upload pdf or images");
      return;
    }

    setFieldValue("attachments", [
      ...attachments,
      ...files.map((document) => ({
        id: uniqueId(),
        document,
      })),
    ]);

    event.target.value = "";
  };

  const fileClickHandler = (document) => {
    setDocumentToPreview({
      documentTypeName: document.name,
      url: URL.createObjectURL(document),
      documentType: getFileExtension(document.name),
      documentName: removeExtension(document.name ?? ""),
    });
  };

  if (isBuildingInfoLoading) {
    return <Backdrop open={isBuildingInfoLoading} />;
  }

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}
      {openMailForm && (
        <Modal
          isOpen
          title="Mail Information"
          maxWidth="md"
          handleClose={() => setOpenMailForm(false)}
        >
          <Grid container spacing={2} className="mt-2">
            <Grid item xs={12}>
              <InputFieldText
                label={t("Subject") + "*"}
                name="subject"
                placeholder="Enter subject"
                formik={formik}
              />
            </Grid>

            <Grid item xs={12}>
              <InputFieldText
                label={t("Description") + "*"}
                placeholder="Enter Description"
                name="body"
                formik={formik}
                multiline
                rows={6}
                InputProps={{
                  endAdornment: (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                      }}
                    >
                      <IconButton color="primary" component="label">
                        <AttachmentIcon />
                        <input
                          style={{
                            display: "none",
                          }}
                          accept="image/*, .pdf, .PDF"
                          type="file"
                          onChange={fileChangeHandler}
                          multiple="multiple"
                        />
                      </IconButton>
                    </div>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <div className="mb-1">
                <StyledTextField
                  className="w-100"
                  inputRef={ccRef}
                  error={!!formik.errors.ccEmails}
                  size="small"
                  value={ccEmails}
                  label={t("CC")}
                  name="ccEmails"
                  placeholder="Type Email And Press Enter"
                  onChange={(value) => {
                    setFieldValue("ccEmails", value);
                  }}
                  clearInputOnBlur
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="Add Email">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => {
                            const email = ccRef.current?.value?.trim();
                            if (!email) return;

                            setFieldValue("ccEmails", [...ccEmails, email]);
                          }}
                          edge="end"
                          color="primary"
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />

                <Typography
                  variant="subtitle2"
                  className={`${
                    formik.errors.ccEmails ? "visible" : "invisible"
                  } input-field-error-message`}
                  color="error"
                >
                  {formik.errors.ccEmails}
                </Typography>
              </div>
            </Grid>

            {!!attachments.length && (
              <>
                <Grid item xs={12}>
                  <Typography className="mt-2 fw-semibold">
                    {t("Documents")} :
                  </Typography>
                </Grid>
                <Grid item xs={12} className="mb-3 ms-1 mt-1">
                  <div className="d-flex gap-3 flex-wrap mt-2">
                    {attachments.map(({ document, id }) => (
                      <div key={id} className="image-container">
                        <Box
                          component="img"
                          className="image d-block m-auto"
                          alt="document image"
                          src={
                            getFileExtension(document.name) === "pdf"
                              ? pdfImg
                              : URL.createObjectURL(document)
                          }
                          onClick={() => fileClickHandler(document)}
                        />
                        <i
                          className="ri-close-circle-line fs-4 close-btn"
                          onClick={() =>
                            setFieldValue(
                              "attachments",
                              attachments.filter((a) => a.id !== id)
                            )
                          }
                        />
                        <Tooltip title={document.name}>
                          <DocumentName fontSize={13} variant="body2">
                            {document.name}
                          </DocumentName>
                        </Tooltip>
                      </div>
                    ))}
                  </div>
                </Grid>
              </>
            )}

            <Grid item xs={12} textAlign="right">
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={isMailSending}
              >
                {t("Save")}
                {isMailSending && (
                  <CircularProgress size={20} className="ms-2" />
                )}
              </Button>
            </Grid>
          </Grid>
        </Modal>
      )}

      <Modal
        isOpen
        handleClose={handleClose}
        title={t("Send Mail")}
        maxWidth="lg"
      >
        <StyledGrid
          checkboxSelection
          autoHeight
          disableRowSelectionOnClick
          rows={buildingInfo}
          columns={columns}
          pageSizeOptions={[10, 50, 100]}
          rowCount={buildingInfo?.length}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowSelectionModelChange={(ids) => {
            const selectedEmails = buildingInfo
              .filter((row) => ids.includes(row.id))
              .map((row) => row.email)
              .filter(Boolean);

            setFieldValue(
              "recipientEmails",
              Array.from(new Set(selectedEmails))
            );
          }}
          slots={{
            columnMenu: CustomColumnMenu,
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {t("No records found")}.
              </Stack>
            ),
          }}
        />

        <div className="text-end mt-2">
          <Button
            variant="contained"
            color="success"
            disabled={!recipientEmails.length}
            onClick={() => setOpenMailForm(true)}
          >
            {t("Send")}
          </Button>
        </div>
      </Modal>
    </>
  );
};

SendBuildingMailModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  t: PropTypes.func,
};

export default withTranslation()(SendBuildingMailModal);
